import { isNilOrEmpty } from "@salespark/toolkit";
import React, { useEffect, useRef, useState } from "react";
const env = process.env.REACT_APP_ENVIRONMENT || "production";
const LIBRARY_ID = 133254;
const BUNNYNET_STREAM_LIBRARY_TIERS = { premium: 133254, basic: 464074, starter: 464077, internal: 11494 };
const isSmallDevice = window.innerWidth < 1400;

const ensurePlayerjsSendNullGuardOnce = () => {
  try {
    const PlayerConstructor = window.playerjs?.Player;
    if (!PlayerConstructor || PlayerConstructor.__sendNullGuardInstalled) {
      return;
    }
    const originalSend = PlayerConstructor.prototype.send;
    PlayerConstructor.prototype.send = function guardedPlayerjsSend() {
      try {
        if (this.loaded === true && (!this.elem || this.elem.contentWindow == null)) {
          return true;
        }
        return originalSend.apply(this, arguments);
      } catch (error) {
        return true;
      }
    };
    PlayerConstructor.__sendNullGuardInstalled = true;
  } catch (error) {
    // Silent: guard is best-effort only
  }
};

const VideoLessonBunnyV2 = ({ videoData, details, updateVideoCurrentTimeHandler, progressHandler }) => {
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [videoInfo, setVideoInfo] = useState(null);
  const videoInfoRef = useRef(null);
  const playerRef = useRef(null);

  const tier = videoData?.video_tier || "premium";
  let libraryId = BUNNYNET_STREAM_LIBRARY_TIERS[tier] || LIBRARY_ID;
  const lastUpdateTimestampRef = useRef(0);
  const lastProgressTimestampRef = useRef(0);
  // const videoBlocked = videoData?.video_blocked === true;
  let iFrameAllow = "accelerometer; encrypted-media; picture-in-picture";

  // // Check if video is blocked and videoData is an object
  // if (videoBlocked === true && typeof videoData === "object") {
  //   libraryId = 11494;
  //   videoData.video_id = "74df7a40-f497-4f59-baf2-58ab25374d31?autoplay=true&loop=true&muted=true&preload=true&responsive=true";
  //   iFrameAllow = "accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;";
  // }

  useEffect(() => {
    if (!details || details.video_origin !== "library" || !videoData?.video_id) {
      console.log("Invalid video details or unsupported origin.");
      return;
    }

    videoInfoRef.current = null;

    let disposed = false;
    const isStale = () => disposed;

    const playerScriptUrl = "//assets.mediadelivery.net/playerjs/player-0.1.0.min.js";

    const onError = (e) => {
      if (isStale()) return;
      console.error("Player error:", e);
    };

    const onReady = () => {
      if (isStale()) return;
      if (env !== "production") console.log("Player ready.");
      setLoading(false);
    };

    const onTimeUpdate = (data) => {
      if (isStale()) return;
      try {
        const info = typeof data === "string" ? JSON.parse(data) : data;
        const currentTime = info.seconds;
        const totalDuration = info.duration;

        const now = Date.now();

        // Progress signal for the UI (e.g. the Flix "up next" countdown). The player
        // already reports ~4x/s; emit about once a second so a countdown stays smooth.
        // Deliberately SEPARATE from the API save below: detecting progress and
        // persisting it to the DB are two different cadences (the save stays at 30s).
        if (now - lastProgressTimestampRef.current >= 1000) {
          lastProgressTimestampRef.current = now;
          videoInfoRef.current = { currentTime, totalDuration };
          progressHandler(currentTime, totalDuration);
        }

        // Persist current time to the API — throttled to once every 30s (avoid DB spam).
        if (now - lastUpdateTimestampRef.current < 30000) return;
        lastUpdateTimestampRef.current = now;
        setVideoInfo({ currentTime, totalDuration });
        updateVideoCurrentTimeHandler(videoData?.video_id, currentTime);
      } catch (error) {
        console.error("Error processing player data:", error);
      }
    };

    const onPlay = () => {
      if (isStale()) return;
      if (env !== "production") console.log("Video playing");
    };

    const onPause = () => {
      if (isStale()) return;
      if (env !== "production") console.log("Video paused");
    };

    const onEnded = () => {
      if (isStale()) return;
      const latestVideoInfo = videoInfoRef.current;
      if (!latestVideoInfo) return;

      updateVideoCurrentTimeHandler(videoData?.video_id, latestVideoInfo.totalDuration);
      progressHandler(latestVideoInfo.currentTime, latestVideoInfo.totalDuration, true);
    };

    const detachPlayerListeners = (player) => {
      if (!player || typeof player.off !== "function") {
        return;
      }
      player.off("error", onError);
      player.off("ready", onReady);
      player.off("timeupdate", onTimeUpdate);
      player.off("play", onPlay);
      player.off("pause", onPause);
      player.off("ended", onEnded);
    };

    // Load Player.js once if the script tag is not already present
    const existing = document.querySelector(`script[src="${playerScriptUrl}"]`);
    const loadScript = () =>
      new Promise((resolve) => {
        if (existing) return resolve();
        const script = document.createElement("script");
        script.src = playerScriptUrl;
        script.onload = resolve;
        document.body.appendChild(script);
      });

    loadScript()
      .then(() => {
        if (isStale()) return;

        setLoading(false);
        if (!window.playerjs) {
          console.error("playerjs not available");
          return;
        }

        ensurePlayerjsSendNullGuardOnce();

        const iframeEl = iframeRef.current;
        if (!iframeEl || iframeEl.nodeName !== "IFRAME" || !iframeEl.getAttribute("src")) {
          return;
        }

        const previousPlayer = playerRef.current;
        if (previousPlayer) {
          detachPlayerListeners(previousPlayer);
          playerRef.current = null;
        }

        let player;
        try {
          player = new window.playerjs.Player(iframeEl);
        } catch (error) {
          console.error("Error initializing player:", error);
          return;
        }

        if (isStale()) {
          detachPlayerListeners(player);
          return;
        }

        playerRef.current = player;

        player.on("error", onError);
        player.on("ready", onReady);
        player.on("timeupdate", onTimeUpdate);
        player.on("play", onPlay);
        player.on("pause", onPause);
        player.on("ended", onEnded);
      })
      .catch((error) => {
        if (isStale()) return;
        console.log("Error loading player script:", error);
        setLoading(false);
      });

    return () => {
      disposed = true;
      const activePlayer = playerRef.current;
      playerRef.current = null;
      if (activePlayer) {
        detachPlayerListeners(activePlayer);
      }
    };
  }, [details, videoData?.video_id]);

  if (isNilOrEmpty(videoData?.video_id)) {
    return <></>;
  }
  return (
    <div className={`lesson-video-container ${isSmallDevice ? "ar-full" : ""}`} id="lesson-video-container">
      <div className={`${isSmallDevice ? "ar-full" : "ar_1_6-9"}`} style={{ position: "relative", paddingTop: "56.25%", display: "flex" }}>
        <iframe
          id="bunny-stream-embed"
          ref={iframeRef}
          loading="lazy"
          title={videoData?.title}
          src={videoData ? `https://iframe.mediadelivery.net/embed/${libraryId}/${videoData.video_id}` : ""}
          allow={iFrameAllow}
          allowFullScreen
          style={{ border: "none", position: "absolute", top: 0, height: "100%", width: "100%" }}
        ></iframe>
      </div>
    </div>
  );
};

export default VideoLessonBunnyV2;
